import { AircraftClass, type AircraftClassId } from './airlines';
import { dollars } from './buildings';
import type { Rng } from './rng';

/*
 * VIP passengers: now and then a famous name is booked on a flight already on the board (a
 * statesman, a rock star, a business leader on a small jet or a helicopter; a football team on
 * an airliner). The airport hears of it in advance; if the flight leaves on time with everyone
 * aboard, the VIP's office pays a thank-you and the news is all praise. A late or cancelled
 * flight makes headlines of the wrong kind.
 */

/** Who is coming. Saved on the flight: append only (0: nobody special). */
export const VipKind = {
  None: 0,
  Statesman: 1,
  RockStar: 2,
  BusinessLeader: 3,
  FootballTeam: 4,
} as const;
export type VipKindId = (typeof VipKind)[keyof typeof VipKind];

/** Chance an hour (percent) of a VIP booking, from 07:00 to 18:59, with one at most at a time. */
export const VIP_HOURLY_PERCENT = 9;
export const VIP_FIRST_HOUR = 7;
export const VIP_LAST_HOUR = 18;
/** The tower level a VIP expects. */
export const VIP_TOWER_LEVEL = 2;
/** The flight must still be at least this far from landing when the VIP is announced. */
export const VIP_NOTICE_MINUTES = 60;
/** Late by at most this much (the airport's share) and the VIP is pleased. */
export const VIP_ON_TIME_MINUTES = 10;
/** The talk around the airport either way. */
export const VIP_PRAISE = 200;
export const VIP_SCORN = -150;

const REWARD: Record<number, number> = {
  [VipKind.Statesman]: dollars(60_000),
  [VipKind.RockStar]: dollars(40_000),
  [VipKind.BusinessLeader]: dollars(50_000),
  [VipKind.FootballTeam]: dollars(80_000),
};

/** The VIP's office pays this when it went well. */
export function vipReward(kind: number): number {
  return REWARD[kind] ?? 0;
}

/** Who might be aboard a flight of this class (a team needs an airliner). */
export function vipKindsFor(cls: AircraftClassId): VipKindId[] {
  switch (cls) {
    case AircraftClass.Light:
    case AircraftClass.Helicopter:
      return [VipKind.Statesman, VipKind.RockStar, VipKind.BusinessLeader];
    case AircraftClass.Regional:
    case AircraftClass.Narrowbody:
      return [VipKind.FootballTeam, VipKind.RockStar];
    case AircraftClass.Widebody:
    case AircraftClass.Jumbo:
      return [VipKind.FootballTeam, VipKind.Statesman];
    default:
      return [];
  }
}

/** Whether a VIP booking comes this hour. */
export function rollVip(rng: Rng): boolean {
  return rng.int(100) < VIP_HOURLY_PERCENT;
}

/** One of the kinds, evenly. */
export function pickVip(rng: Rng, kinds: readonly VipKindId[]): VipKindId {
  return kinds[rng.int(kinds.length)] ?? VipKind.None;
}

/** Whether the VIP leaves pleased: on time and nobody of theirs left behind. */
export function vipPleased(delayMinutes: number, missed: number): boolean {
  return delayMinutes <= VIP_ON_TIME_MINUTES && missed === 0;
}
